import { useEffect, useRef, useState } from 'react';
import { Sparkles, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 15000;

const relTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(1, Math.floor(diff / 1000));
  if (s < 60) return `há ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `há ${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
};

/**
 * Mural de Capturas — live-ish feed of Pokémon captured by students in a classroom.
 * Polls every 15s. Highlights new items with a subtle entrance animation.
 *
 * Props:
 *   - classroomId: when provided, calls the teacher endpoint
 *   - endpoint:    when provided, calls that endpoint directly (full path after /api)
 *                  Example for student: "/student/journey-feed"
 *   - compact:     reduces vertical density (used in dashboards)
 */
export default function CaptureFeed({ classroomId, endpoint, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newIds, setNewIds] = useState(new Set());
  const seenIds = useRef(new Set());

  const fetchFeed = async () => {
    try {
      const url = endpoint
        ? `${BACKEND_URL}/api${endpoint}`
        : `${BACKEND_URL}/api/teacher/classrooms/${classroomId}/journey-feed`;
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      const fetched = d.items || [];
      const fresh = new Set();
      for (const it of fetched) {
        if (!seenIds.current.has(it.feed_id)) fresh.add(it.feed_id);
      }
      setItems(fetched);
      fetched.forEach((it) => seenIds.current.add(it.feed_id));
      if (loading) {
        setLoading(false);
      } else if (fresh.size > 0) {
        setNewIds(fresh);
        setTimeout(() => setNewIds(new Set()), 5000);
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    fetchFeed();
    const t = setInterval(fetchFeed, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [classroomId, endpoint]);

  return (
    <aside className={`bg-white rounded-2xl border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] ${compact ? 'p-3' : 'p-4'} ${compact ? '' : 'sticky top-4'}`} data-testid="capture-feed">
      <style>{`
        .pokeclass-feed-scroll { scrollbar-width: thin; scrollbar-color: transparent transparent; transition: scrollbar-color 0.3s ease; }
        .pokeclass-feed-scroll:hover { scrollbar-color: #f59e0b #fef3c7; }
        .pokeclass-feed-scroll::-webkit-scrollbar { width: 6px; }
        .pokeclass-feed-scroll::-webkit-scrollbar-track { background: transparent; border-radius: 8px; }
        .pokeclass-feed-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 8px; transition: background 0.3s ease; }
        .pokeclass-feed-scroll:hover::-webkit-scrollbar-track { background: #fef3c7; }
        .pokeclass-feed-scroll:hover::-webkit-scrollbar-thumb { background: #f59e0b; }
        .pokeclass-feed-scroll::-webkit-scrollbar-thumb:hover { background: #d97706; }
      `}</style>
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-5 h-5 text-amber-500"/>
        <h3 className="font-outfit font-black text-base">Mural de Capturas</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-400 font-outfit font-black">Ao vivo</span>
      </div>

      {loading ? (
        <p className="text-center text-xs text-slate-400 italic py-6">Carregando…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-8">
          <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
          <p className="text-xs text-slate-500 italic">Sem capturas ainda. As capturas dos seus amigos vão aparecer aqui assim que eles concluírem uma jornada.</p>
        </div>
      ) : (
        <ul className={`space-y-2 ${compact ? 'max-h-[320px]' : 'max-h-[70vh]'} overflow-y-auto pr-1 pokeclass-feed-scroll`} data-testid="capture-feed-list">
          {items.map((it) => {
            const isNew = newIds.has(it.feed_id);
            return (
              <li
                key={it.feed_id}
                data-testid={`capture-feed-item-${it.feed_id}`}
                className={`relative flex items-center gap-3 p-2 rounded-xl border-2 ${
                  isNew
                    ? 'border-amber-500 bg-gradient-to-r from-amber-50 to-rose-50 animate-pulse-slow'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                {isNew && (
                  <span className="absolute -top-1 -right-1 text-[9px] uppercase font-outfit font-black bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded-full border-2 border-slate-900">
                    NOVO
                  </span>
                )}
                {/* Pokémon sprite */}
                <img
                  src={it.sprite_url}
                  alt={it.pokemon_name}
                  loading="lazy"
                  decoding="async"
                  className="w-12 h-12 shrink-0"
                  style={{ imageRendering: 'pixelated' }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs leading-tight">
                    <span className="font-outfit font-black">{it.student_name}</span>
                    <span className="text-slate-500"> capturou </span>
                    <span className="font-outfit font-black text-rose-600">{it.pokemon_name}</span>
                    {it.first_capture && <span className="ml-1 text-[10px] text-amber-700 font-bold">★ Primeira vez</span>}
                  </p>
                  <p className="text-[10px] text-slate-500 italic truncate">
                    na jornada <strong>{it.journey_title}</strong>
                  </p>
                  <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                    <span>{relTime(it.created_at)}</span>
                    <span>·</span>
                    <span>{it.accuracy}% de acerto</span>
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
